import { useEffect, useRef } from 'react'

import axios from 'axios'
import Cookies from 'js-cookie'

import { getWindowId, locale } from 'api/utils'
import { getSubotCookieId, getUserInfoFromCookie } from 'api/utils/cookie'
import { GA_TOKEN_COOKIE, IS_PRODUCTION } from 'interfaces/constants'
import { LOCALE } from 'interfaces/types/Locale'

import { useCurrentUrl } from './useCurrentUrl'
import { useIsMobile } from './useIsMobile'

export const baseURL = IS_PRODUCTION
  ? 'https://event-tracking.hellohealthgroup.com'
  : 'https://event-tracking-staging.hellohealthgroup.com'

export const SITE_CODE = {
  [LOCALE.Vietnam]: 'hellobacsi',
  [LOCALE.Indonesia]: 'hellosehat',
  [LOCALE.Thailand]: 'hellokhunmor',
  [LOCALE.Malaysia]: 'hellodoktor',
  [LOCALE.Taiwan]: 'helloyishi',
  [LOCALE.Myanmar]: 'hellosayarwon',
  [LOCALE.PhilippinesEnglish]: 'hellodoctor',
  [LOCALE.Cambodia]: 'hellokrupet',
  [LOCALE.India]: 'helloswasthya',
  [LOCALE.PhilippinesTagalog]: 'hellodoctor-fil',
  [LOCALE.Singapore]: 'hellojiankang',
}

export function useTrackingVisitSite() {
  const currentUrl = useCurrentUrl()

  useEffect(() => {
    // Track everytime user visit the site
    const info = getUserInfoFromCookie()
    axios
      .post(baseURL + '/v1/custom-event/collect', {
        action: 'start',
        source: 'Discover',
        ga_client_id: Cookies.get(GA_TOKEN_COOKIE),
        site_code: SITE_CODE[locale],
        email: info.email,
        account_id: info.id,
        data: {
          timestamp: new Date().toISOString(),
          page_url: currentUrl,
          window_id: getWindowId(),
          cookie_id: getSubotCookieId(),
          client_agent: navigator?.userAgent || '',
          token: localStorage.getItem('fcm_token') || '',
          email: info.email,
          account_id: info.id,
        },
      })
      .catch(console.log)
  }, [])
}

export function useTrackingGetFcmToken() {
  const currentUrl = useCurrentUrl()

  const trackingGetFcmToken = (currentToken: string) => {
    if (localStorage.getItem('last_track') === currentToken) return
    const info = getUserInfoFromCookie()
    axios
      .post(baseURL + '/v1/custom-event/collect', {
        action: 'get_token',
        source: 'Discover',
        ga_client_id: Cookies.get(GA_TOKEN_COOKIE),
        site_code: SITE_CODE[locale],
        email: info.email,
        account_id: info.id,
        data: {
          timestamp: new Date().toISOString(),
          page_url: currentUrl,
          window_id: getWindowId(),
          cookie_id: getSubotCookieId(),
          client_agent: navigator?.userAgent || '',
          token: currentToken,
          email: info.email,
          account_id: info.id,
        },
      })
      .then(() => {
        localStorage.setItem('last_track', currentToken)
      })
      .catch(console.log)
  }

  return { trackingGetFcmToken }
}

export const useTrackingClickCarePathTemplate = () => {
  const currentUrl = useCurrentUrl()
  const isMobile = useIsMobile()

  const trackFn = (target_group: string) => {
    const userInfo = getUserInfoFromCookie()
    const timestamp = new Date().toISOString()
    axios
      .post(baseURL + '/v1/custom-event/collect', {
        action: 'submit',
        source: 'spotlight',
        ga_client_id: Cookies.get(GA_TOKEN_COOKIE),
        email: userInfo?.email || '',
        account_id: userInfo?.id,
        site_code: SITE_CODE[locale],
        timestamp,
        data: {
          timestamp,
          url: currentUrl,
          window_id: getWindowId(),
          cookie_id: getSubotCookieId(),
          client_agent: navigator?.userAgent || '',
          token: localStorage.getItem('fcm_token'),
          target_group,
          device_type: isMobile ? 'mobile' : 'web',
        },
      })
      .catch(console.log)
  }

  const trackFnRef = useRef(trackFn)

  return { trackUserClickCarePathTemplate: trackFnRef.current }
}

export const useTrackingClickButtonTemplate = () => {
  const currentUrl = useCurrentUrl()
  const isMobile = useIsMobile()

  const trackFn = (customEvent: string) => {
    const userInfo = getUserInfoFromCookie()
    const timestamp = new Date().toISOString()
    axios
      .post(baseURL + '/v1/custom-event/collect', {
        action: customEvent,
        source: 'landing_page',
        ga_client_id: Cookies.get(GA_TOKEN_COOKIE),
        email: userInfo?.email || '',
        account_id: userInfo?.id,
        site_code: SITE_CODE[locale],
        timestamp,
        data: {
          timestamp,
          url: currentUrl,
          window_id: getWindowId(),
          cookie_id: getSubotCookieId(),
          client_agent: navigator?.userAgent || '',
          token: localStorage.getItem('fcm_token'),
          device_type: isMobile ? 'mobile' : 'web',
        },
      })
      .catch(console.log)
  }

  const trackFnRef = useRef(trackFn)

  return { trackingClickButtonTemplate: trackFnRef.current }
}
